import { forwardRef } from 'react';
import type { FunctionComponent, PropsWithoutRef, Ref, MouseEvent } from 'react';
import styled, { css } from 'styled-components';
import { hideVisually } from 'polished';

import { useConsolidatedRef } from '../../../hooks';
import { defaultThemeProp } from '../../../theme';
import { StyledFormControl } from '../../FormControl';
import Flex from '../../Flex';
import type { ForwardProps } from '../../../types';
import { StyledSelectInput } from '../ComboBox.styles';
import { isInstance } from '../../../utils';

import type SingleSelectInputProps from './SingleSelectInput.types';

const StyledPrimaryContainer = styled.div``;

const StyledInput = styled.input`
  &[aria-expanded='false']:has(+ ${StyledPrimaryContainer}) {
    ${hideVisually}
  }
`;

const StyledVisualContainer = styled.span(props => {
  const {
    theme: {
      base,
      components: {
        'form-control': formControl,
        input: { height }
      }
    }
  } = props;

  return css`
    min-width: calc(${height} - ${formControl['border-width']});
    padding: 0 calc(0.5 * ${base.spacing});
    height: calc(${height} - 2 * ${formControl['border-width']});

    & + input {
      margin-inline-start: 0;
    }
  `;
});

StyledVisualContainer.defaultProps = defaultThemeProp;

export const StyledSingleSelectInput = styled(StyledSelectInput)`
  &:has(input[aria-expanded='true']) {
    ${StyledPrimaryContainer} {
      ${hideVisually}
    }
  }
`;

const SingleSelectInput: FunctionComponent<SingleSelectInputProps & ForwardProps> = forwardRef(
  function SingleSelectInput(
    props: PropsWithoutRef<SingleSelectInputProps>,
    ref: Ref<HTMLInputElement>
  ) {
    const {
      value,
      readOnly,
      disabled,
      status,
      actions,
      onChange,
      onResolveSuggestion,
      selected,
      visual,
      ...restProps
    } = props;
    const inputRef = useConsolidatedRef(ref);

    return (
      <Flex
        as={StyledSingleSelectInput}
        forwardedAs={StyledFormControl}
        hasSuggestion={status === 'pending' && !!onResolveSuggestion}
        container={{ alignItems: 'center', wrap: 'nowrap' }}
        disabled={disabled}
        status={status}
        readOnly={readOnly}
      >
        {visual && (
          <Flex
            container={{ justify: 'center', alignItems: 'center' }}
            as={StyledVisualContainer}
            onClick={() => {
              inputRef.current?.focus();
              inputRef.current?.click();
            }}
          >
            {visual}
          </Flex>
        )}
        <StyledInput
          disabled={disabled}
          size={1}
          value={value || selected?.text || ''}
          onChange={onChange || (() => {})}
          readOnly={readOnly || !onChange}
          autoComplete='_off'
          {...restProps}
          ref={inputRef}
        />
        {selected?.primary && (
          <Flex
            as={StyledPrimaryContainer}
            container={{
              alignItems: 'center',
              justify: 'between',
              gap: 2,
              pad: [undefined, 1]
            }}
            item={{
              grow: 1
            }}
            onClick={(e: MouseEvent<HTMLDivElement>) => {
              if (!inputRef.current) return;

              const interactiveEl = isInstance(e.target, Element)
                ? e.target.closest('a') || e.target.closest('button')
                : null;

              if (!interactiveEl) {
                inputRef.current.focus();
                inputRef.current.click();
              }
            }}
          >
            {selected.primary}
          </Flex>
        )}
        {actions}
      </Flex>
    );
  }
);

export default SingleSelectInput;
